/**
 * ZA Player Pro — PWA Install Prompt (v1.4.77)
 *
 * Two-mode floating install button:
 *
 * 1. Chrome/Edge/Android/Samsung Internet — captures the native
 *    `beforeinstallprompt` event and offers a one-tap install.
 *
 * 2. iOS Safari — no native prompt exists on iOS, so we detect
 *    iOS + Safari + not-already-installed, and show a small
 *    dismissible banner explaining the Share → Add-to-Home-Screen
 *    flow (with an inline share-icon SVG).
 *
 * The banner remembers dismissal for 30 days via localStorage
 * (`zapp_pwa_dismissed_at`).
 *
 * Mount once at the app root — it self-hides on:
 *   - Capacitor / native shells (already an app)
 *   - Standalone display-mode (already installed)
 *   - Recent dismissal (< 30 days)
 */

import React, { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";

const DISMISS_KEY = "zapp_pwa_dismissed_at";
const DISMISS_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function isRunningStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  if (window.navigator.standalone) return true; // iOS Safari flag
  return false;
}

function isNativeShell() {
  if (typeof window === "undefined") return false;
  if (window.Capacitor?.isNativePlatform?.()) return true;
  if (window.__ZAPP_IS_ELECTRON__) return true;
  return false;
}

function isIOSSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // Detect Safari specifically (not Chrome/Firefox on iOS which are WebKit
  // wrappers but don't expose Add-to-Home-Screen either — they redirect
  // the user back to Safari). We show the banner on all iOS browsers so
  // the user still gets guidance.
  return isIOS;
}

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = parseInt(raw, 10);
    if (!at) return false;
    return Date.now() - at < DISMISS_MS;
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch { /* private mode */ }
}

export default function PWAInstallPrompt() {
  const [installEvent, setInstallEvent] = useState(null);
  const [showIOSBanner, setShowIOSBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isNativeShell() || isRunningStandalone() || wasRecentlyDismissed()) {
      return;
    }

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);

    // iOS Safari never fires beforeinstallprompt, so trigger the
    // banner ourselves after a short delay (so the app has time to
    // paint first).
    if (isIOSSafari()) {
      const t = setTimeout(() => setShowIOSBanner(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!installEvent) return;
    try {
      installEvent.prompt();
      const { outcome } = await installEvent.userChoice;
      if (outcome === "accepted") {
        setInstallEvent(null);
      } else {
        markDismissed();
        setDismissed(true);
      }
    } catch { /* user closed */ }
  };

  const handleDismiss = () => {
    markDismissed();
    setDismissed(true);
    setInstallEvent(null);
    setShowIOSBanner(false);
  };

  if (dismissed) return null;

  // Chrome / Android / desktop: floating install button (bottom right)
  if (installEvent) {
    return (
      <div
        data-testid="pwa-install-prompt-chrome"
        className="fixed bottom-4 right-4 z-[9999] flex items-center gap-2 rounded-full bg-gradient-to-r from-pink-600 to-fuchsia-600 pl-4 pr-2 py-2 shadow-2xl shadow-pink-900/40 ring-1 ring-white/20 animate-in fade-in slide-in-from-bottom-4"
      >
        <button
          data-testid="pwa-install-btn"
          onClick={handleInstall}
          className="flex items-center gap-2 text-white text-sm font-semibold"
        >
          <Download className="w-4 h-4" />
          Install ZA Player Pro
        </button>
        <button
          data-testid="pwa-install-dismiss-btn"
          onClick={handleDismiss}
          aria-label="Dismiss install prompt"
          className="w-7 h-7 flex items-center justify-center rounded-full text-white/80 hover:text-white hover:bg-white/10"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // iOS Safari: instructional banner (bottom, full width)
  if (showIOSBanner) {
    return (
      <div
        data-testid="pwa-install-prompt-ios"
        className="fixed bottom-4 inset-x-4 z-[9999] flex items-start gap-3 rounded-2xl bg-neutral-950/95 backdrop-blur-xl border border-white/15 p-4 shadow-2xl shadow-black/60 animate-in fade-in slide-in-from-bottom-4"
      >
        <img
          src="/za-logo-icon.png"
          alt=""
          className="w-10 h-10 rounded-lg flex-shrink-0"
        />
        <div className="flex-1 text-white text-sm leading-relaxed">
          <div className="font-semibold mb-1">Install ZA Player Pro</div>
          <div className="text-white/70">
            Tap <Share className="inline w-4 h-4 mx-0.5 -mt-0.5" /> then{" "}
            <span className="font-medium text-white">Add to Home Screen</span>{" "}
            to install this app.
          </div>
        </div>
        <button
          data-testid="pwa-install-ios-dismiss-btn"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="w-7 h-7 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return null;
}
